import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import { Input, Form, Button, message, Modal, Collapse } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { QRCodeSVG } from "qrcode.react";
import { serverFetch, strDate2Server } from "@extensions";
import ChildModal from "@components/family/child-modal";
import ProfileFields from "@components/family/profile-fields";
import "./account.css";
import { useGetIsPhone } from "extensions";
import { useCompany } from "@redux";
import { clientProfileFields } from "@extensions/enums";

const errMsg = (e) => e?.userMessage || e?.UserMessage || "Ошибка запроса";

const hasEmailInProfile = (c) =>
  c?.email != null && String(c.email).trim() !== "";

const Account = ({ visible, onSubmit, onCancel, pendingAction, validationResult }) => {
  const company = useCompany();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState({});
  const isPhone = useGetIsPhone();

  const [verifyEmail, setVerifyEmail] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [authGuid, setAuthGuid] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const backgroundStyle = company?.catalogBackgroundImage
    ? { backgroundImage: `url("${company.catalogBackgroundImage}")` }
    : undefined;
  // const [formValid, setFormValid] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [childModalOpen, setChildModalOpen] = useState(false);
  // null - создаётся новый участник группы; заполнен - редактируется существующий
  const [editingChild, setEditingChild] = useState(null);
  // Раскрытая строка группы: на телефоне действия показываем только по тапу
  const [activeFamilyId, setActiveFamilyId] = useState(null);

  const getData = useCallback(() => {
    setLoading(true);
    serverFetch("OnlineClient/info", { method: "GET" })
      .then((data) => {
        const parsed = data.dateBirth ? dayjs(data.dateBirth) : null;
        const dateBirth = parsed && typeof parsed.isValid === "function" && parsed.isValid() ? parsed : null;
        const newClient = {
          ...data,
          dateBirth,
        };
        setClient(newClient);
        form.setFieldsValue({
          ...newClient,
          dateBirth,
        });
        if (hasEmailInProfile(newClient)) {
          setVerifyEmail("");
          setCodeSent(false);
          setAuthGuid(null);
          setVerifyCode("");
        }
        setLoading(false);
      })
      .catch(() => {
        message.error("Ошибка получения пользователя");
        setLoading(false);
      });
  }, [form]);

  // Загрузка участников группы представителя
  const fetchFamilyData = useCallback(() => {
    serverFetch("OnlineClient/FamilyList", { method: "GET" })
      .then((data) => {
        setFamilyMembers(data || []);
      })
      .catch(() => {
        message.error("Ошибка получения списка группы");
      });
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    // Подтягиваем группу при появлении данных клиента
    if (Object.keys(client).length > 0) {
      fetchFamilyData();
    }
  }, [client, fetchFamilyData]);

  const onFinish = () => {
    setLoading(true);
    const dataToSend = {
      name: client.name,
      dateBirth: client.dateBirth ? strDate2Server(client.dateBirth) : null,
      sex: client.sex
    };
    serverFetch("OnlineClient/info", { method: "PUT", bodyData: dataToSend })
      .then(() => {
        setLoading(false);
        message.success("Профиль сохранен");
        if (onSubmit) {
          onSubmit();
        }
      })
      .catch((e) => {
        setLoading(false);
        message.error(errMsg(e));
      });
  };

  const onAutopayCancel = () => {
    if (window.confirm("Отменить подписку?")) {
      setLoading(true);
      serverFetch("account/AutopayCancel", { method: "POST" })
        .then(getData)
        .catch((e) => {
          setLoading(false);
          message.error(`Ошибка ${e.UserMessage}`);
        });
    }
  };

  const onValuesChange = (value) => {
    setClient({
      ...client,
      ...value,
    });
  };

  const openQrModal = () => {
    if (isPhone && client.card) {
      setIsQrModalOpen(true);
    }
  };

  const onQrKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openQrModal();
    }
  };

  const handleVerifyEmailStart = () => {
    const email = String(verifyEmail || "").trim();
    if (!email || !email.includes("@")) {
      message.warning("Укажите корректный адрес электронной почты");
      return;
    }
    setVerifyLoading(true);
    serverFetch("OnlineClient/VerifyEmailStart", {
      method: "POST",
      bodyData: { email },
    })
      .then((data) => {
        const guid = data?.authCode ?? data?.authGuid;
        if (!guid) {
          message.error("Не получен код авторизации");
          return;
        }
        setAuthGuid(guid);
        setCodeSent(true);
        message.success("Код отправлен");
      })
      .catch((e) => message.error(errMsg(e)))
      .finally(() => setVerifyLoading(false));
  };

  const handleVerifyEmailConfirm = () => {
    const code = String(verifyCode || "").trim();
    if (!code) {
      message.warning("Введите код из письма");
      return;
    }
    if (!authGuid) {
      message.error("Сначала запросите код");
      return;
    }
    setVerifyLoading(true);
    serverFetch("OnlineClient/VerifyEmailConfirm", {
      method: "POST",
      bodyData: { authGuid, code },
    })
      .then(() => {
        message.success("Почта подтверждена");
        window.location.reload();
      })
      .catch((e) => message.error(errMsg(e)))
      .finally(() => setVerifyLoading(false));
  };

  // Генерация правил валидации если указаны обязательные поля 
  const getFormItemValidate = (name, rules) => {
    if (!company) return [];
    
    const requiredFields = company.requireFormForWidget ? (company.requiredClientProfileFields || 0) : 0;
    let fieldRules = [...(rules || [])];
    
    switch (name) {
      case 'name':
        if (requiredFields & clientProfileFields.Name) {
          fieldRules = [{ required: true, message: 'Пожалуйста, введите ваше имя' }, ...fieldRules];
        }
        break;
      case 'dateBirth':
        if (requiredFields & clientProfileFields.Birthday) {
          fieldRules = [{ type: 'object', required: true, message: 'Пожалуйста, выберите дату рождения' }, ...fieldRules];
        }
        break;
      case 'sex':
        if (requiredFields & clientProfileFields.Sex) {
          fieldRules = [{ required: true, message: 'Пожалуйста, укажите ваш пол' }, ...fieldRules];
        }
        break;
      case 'email':
        if (requiredFields & clientProfileFields.Email) {
          fieldRules = [{ type: 'email', required: true, message: 'Пожалуйста, введите ваш email' }, ...fieldRules];
        }
        break;
      default:
        break;
    }
    
    return fieldRules;
  };

  // Проверить, заполнены ли обязательные поля
  const isFormValid = () => {
    if (!company) return true;
    
    const requiredFields = company.requireFormForWidget ? (company.requiredClientProfileFields || 0) : 0;
    const fields = form.getFieldsValue();
    
    if (requiredFields & clientProfileFields.Name) {
      const name = fields.name || '';
      const trimmedName = name.trim();
      if (!trimmedName || /^[\d\s\+\-\(\)]+$/.test(trimmedName)) return false;
    }
    if (requiredFields & clientProfileFields.Birthday) {
      if (!fields.dateBirth) return false;
    }
    if (requiredFields & clientProfileFields.Sex) {
      // Пол: 0 - женщина, поэтому проверяем именно null/undefined
      if (fields.sex === null || fields.sex === undefined) return false;
    }
    if (requiredFields & clientProfileFields.Email) {
      if (!fields.email || fields.email.trim() === '') return false;
    }
    
    return true;
  };

  const qrModal = isPhone && client.card ? (
    <Modal
      open={isQrModalOpen}
      onCancel={() => setIsQrModalOpen(false)}
      footer={null}
      centered
      width="calc(100vw - 24px)"
      wrapClassName="account-qr-modal"
      destroyOnClose
    >
      <div className="account-qr-modal-content">
        <QRCodeSVG value={client.card} size={320} level="H" />
      </div>
    </Modal>
  ) : null;

  // Обработчики управления группой.
  // Участник группы создаётся только новый или редактируется из числа привязанных
  const openChildModal = (child = null) => {
    setEditingChild(child);
    setChildModalOpen(true);
  };
  const closeChildModal = () => {
    setChildModalOpen(false);
    setEditingChild(null);
  };
  const onChildSaved = () => {
    closeChildModal();
    // Перезагружаем список группы
    fetchFamilyData();
  };
  // Кнопка отмены автопродления (само поле рисует ProfileFields);
  const divCard = client.autopayCode ? (
    <Form.Item label=" "><Button onClick={onAutopayCancel}>Отменить подписку</Button></Form.Item>
  ) : null;

  // QR для анкеты: на телефоне выводится под полями и открывает модалку увеличения
  const qrField = !visible && client.card ? (
    <div className="account-qr-code account-qr-code_clickable" role="button" tabIndex={0} onClick={openQrModal} onKeyDown={onQrKeyDown}>
      <QRCodeSVG value={client.card} size={150} level="H" />
    </div>
  ) : null;

  // Поля анкеты - общие со страницей редактирования участника группы, чтобы вид и порядок полей совпадали.
  const divPhone = (
    <ProfileFields
      company={company}
      phone={client.phone}
      autopayCard={client.autopayCode}
      nameDisabled={loading}
      qr={isPhone ? qrField : null}
    />
  );


  // Блок "Группа"
  const familyBlock = (
    <Form.Item label=" " className="account-family-form-item">
      <Collapse ghost defaultActiveKey={["family"]}>
        <Collapse.Panel header={`Группа (${familyMembers.length})`} key="family">
          <div className="account-family-body">
            {familyMembers.length > 0 ? (
              familyMembers.map(member => (
                <div
                  key={member.id}
                  className={`account-family-item ${activeFamilyId === member.id ? "account-family-item_active" : ""}`}
                  onClick={() => setActiveFamilyId(activeFamilyId === member.id ? null : member.id)}
                >
                  <div className="account-family-avatar">
                    {member.name ? member.name[0] : '?'}
                  </div>
                  <div className="account-family-info">
                    <div className="account-family-name" title={member.name}>{member.name}</div>
                    <div className="account-family-meta">
                      {member.dateBirth && <span>{dayjs(member.dateBirth).format("DD.MM.YYYY")}</span>}
                      {member.phone && <span>{member.phone}</span>}
                    </div>
                  </div>
                  <div className="account-family-actions" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="small"
                      className="account-family-action account-family-edit"
                      onClick={() => openChildModal(member)}
                    >
                      {isPhone ? <EditOutlined /> : "Изменить"}
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="account-family-empty">Список пуст</p>
            )}
          </div>
          <div className="account-family-add">
            <Button type="dashed" onClick={() => openChildModal(null)}>
              + Добавить участника группы
            </Button>
          </div>
        </Collapse.Panel>
      </Collapse>
    </Form.Item>
  );

  // Модалка участника группы - общая с корзиной, чтобы проверка обязательных полей была одна
  const childModal = (
    <ChildModal
      open={childModalOpen}
      child={editingChild}
      onClose={closeChildModal}
      onSaved={onChildSaved}
    />
  );

  const pendingActionMessage = pendingAction ? (
    <div className="account-pending-action">
      <span className="account-pending-action-icon">⚠️</span>
      <span className="account-pending-action-text">Для продолжения данного действия, пожалуйста, заполните данные анкеты</span>
    </div>
  ) : null;
  const formContent = (
    <div className="account-form account-form-root">
      {pendingActionMessage}
      {visible && onCancel && (
        <div className="account-modal-close" onClick={onCancel}>
          ✕
        </div>
      )}
      <Form
        colon={false}
        form={form}
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 17 }}
        onValuesChange={onValuesChange}
        onFinish={onFinish}
      >
        <div className="auth-title">Мои данные</div>
        <div className="account-content-wrapper">
          <div className="account-form-inner">
            {divPhone}
            {divCard}
            {/* Семейный блок не показываем в модальном режиме и детям */}
            {!visible && !client.representativeId && familyBlock}
            <Form.Item label={isPhone ? "" : " "}>
              <div className="account-modal-buttons">
                <Button className="btn-blue-raund" size="middle" loading={loading} htmlType="submit" disabled={!isFormValid()}>
                  {visible ? 'ОК' : 'Сохранить'}
                </Button>
                {visible && onCancel && (
                  <Button onClick={onCancel} className="account-modal-cancel">Отмена</Button>
                )}
              </div>
            </Form.Item>
            <label className="prompt">
              Нажимая на кнопку &quot;{visible ? 'ОК' : 'Сохранить'}&quot;, вы даете согласие на обработку{" "}
              <a href="/article/agreement" target="_blank" rel="noreferrer">
                персональных данных
              </a>{" "}
              и соглашаетесь с{" "}
              <a href="/article/rules" target="_blank" rel="noreferrer">
                Условиями и правилами
              </a>{" "}
              сервиса
            </label>
          </div>
          {!visible && client.card && !isPhone && (
            <div className="account-qr-code">
              <QRCodeSVG value={client.card} size={200} level="H" />
            </div>
          )}
        </div>
        {!hasEmailInProfile(client) && (
          <div className="account-content-wrapper account-email-verify-outer">
            <div className="account-form-inner account-email-verify-inner">
              <div className="auth-title account-email-verify-title">Подтверждение почты</div>
              <Form.Item label="Почта" name="email" validateTrigger={['onBlur', 'onChange']} rules={getFormItemValidate('email')}>
                <Input
                  type="email"
                  value={verifyEmail}
                  onChange={(e) => setVerifyEmail(e.target.value)}
                  disabled={codeSent}
                  placeholder="name@example.com"
                  maxLength={200}
                />
              </Form.Item>
              {!codeSent && (
                <Form.Item label=" ">
                  <Button
                    type="primary"
                    loading={verifyLoading}
                    onClick={handleVerifyEmailStart}
                  >
                    Получить код
                  </Button>
                </Form.Item>
              )}
              {codeSent && (
                <>
                  <Form.Item label=" " className="account-email-verify-hint-item">
                    <p className="account-email-verify-hint">
                      На указанную почту был отправлен код авторизации — введите его.
                    </p>
                  </Form.Item>
                  <Form.Item label="Код авторизации">
                    <Input
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value)}
                      placeholder="Код из письма"
                      maxLength={32}
                      autoComplete="one-time-code"
                    />
                  </Form.Item>
                  <Form.Item label=" ">
                    <Button
                      type="primary"
                      loading={verifyLoading}
                      onClick={handleVerifyEmailConfirm}
                    >
                      Подтвердить
                    </Button>
                  </Form.Item>
                </>
              )}
            </div>
            {client.card && !isPhone && (
              <div className="account-email-verify-qr-spacer" aria-hidden />
            )}
          </div>
        )}
      </Form>
      {qrModal}
    </div>
  );

  return (<div className={`auth-context ${visible ? "account-modal" : ""}`} style={visible ? undefined : backgroundStyle}>{formContent}{childModal}</div>);
};

export default Account;
